import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, MapPin, MessageCircle, CalendarDays } from "lucide-react";
import { IMAGES, HERO_VIDEO, HOME_SERVICES, SOCIAL } from "@/data/site";

const WHATSAPP_COMMUNAUTE = SOCIAL.find((x) => x.id === "whatsapp-communaute").url;
import { useSeo, absolute } from "@/lib/seo";
import { Reveal } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import { EventCard, isSpeciale } from "@/components/EventCard";
import { CielDuMoment } from "@/components/CielDuMoment";
import { BookingDialog } from "@/components/BookingDialog";
import { HomeServices, HomeSecondary } from "@/components/HomeServices";
import { HomeStages } from "@/components/HomeStages";
import { HomeRealisations } from "@/components/HomeRealisations";
import { HomeQuickAccess } from "@/components/HomeQuickAccess";
import { SoinDuMomentEncart } from "@/components/SoinDuMomentEncart";
import { HomeEditionsEncart } from "@/components/HomeEditionsEncart";
import { GoogleReviews } from "@/components/GoogleReviews";
import { api } from "@/lib/api";
import { PORTRAITS } from "@/data/blog";
import { useLocale } from "@/context/LocaleContext";
import { CerclesPaiensAnnouncement } from "@/components/CerclesPaiensAnnouncement";
import { CarnetPromo } from "@/components/CarnetPromo";

const Hero = () => {
  const { t } = useLocale();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.85], [1, 0.15]);

  return (
    <section ref={ref} className="relative min-h-screen bg-earth grain overflow-hidden flex items-end" data-testid="hero">
      <motion.div style={{ y }} className="absolute inset-0">
        <video
          className="w-full h-[115%] object-cover opacity-60"
          autoPlay muted loop playsInline preload="metadata"
          poster={HERO_VIDEO.poster}
          aria-hidden="true"
          data-testid="hero-video"
        >
          <source src={HERO_VIDEO.mp4_1080} type="video/mp4" media="(min-width: 1280px)" />
          <source src={HERO_VIDEO.mp4_720} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-earth via-earth/45 to-earth/25" />
      </motion.div>
      <motion.div style={{ opacity }} className="relative z-[2] w-full min-h-screen flex flex-col">
        {/* Ouverture cinématographique : le logo accueille, seul, au centre */}
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6 pt-32 pb-10" data-testid="hero-logo-intro">
          <div className="relative w-28 h-28 sm:w-36 sm:h-36 lg:w-44 lg:h-44">
            <motion.span
              aria-hidden="true"
              initial={{ opacity: 0, scale: 0.4 }}
              animate={{ opacity: [0, 0.55, 0.3], scale: [0.4, 1.35, 1.15] }}
              transition={{ delay: 0.3, duration: 3, ease: "easeOut" }}
              className="absolute inset-0 rounded-full bg-terra/30 blur-3xl"
            />
            <motion.img
              src="/logo.png"
              alt="Entre Les Mondes"
              initial={{ opacity: 0, scale: 0.55, filter: "blur(14px)", rotate: -30 }}
              animate={{ opacity: 1, scale: 1, filter: "blur(0px)", rotate: 0 }}
              transition={{ delay: 0.2, duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full h-full object-contain drop-shadow-[0_0_40px_rgba(174,154,100,0.35)]"
              data-testid="hero-logo"
            />
          </div>
          <motion.p
            initial={{ opacity: 0, y: 18, letterSpacing: "0.3em" }}
            animate={{ opacity: 1, y: 0, letterSpacing: "0.06em" }}
            transition={{ delay: 1.3, duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 font-eccentric text-sand text-4xl sm:text-5xl lg:text-7xl leading-none"
            data-testid="hero-wordmark"
          >
            Entre Les Mondes
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 flex items-center justify-center gap-4 text-terra"
            data-testid="hero-slogan"
          >
            <span aria-hidden="true" className="h-px w-10 sm:w-16 bg-terra/50" />
            <span className="font-serif italic text-lg sm:text-xl lg:text-2xl tracking-[0.08em]">Cosmique · Tellurique</span>
            <span aria-hidden="true" className="h-px w-10 sm:w-16 bg-terra/50" />
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.4, duration: 1.2 }}
            className="eyebrow text-sand/60 mt-6 flex items-center justify-center gap-3"
            data-testid="hero-eyebrow"
          >
            <MapPin className="w-3.5 h-3.5 text-terra" /> {t("Suisse romande")}
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 2.8, duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-7xl mx-auto px-6 lg:px-10 pb-16 lg:pb-20 flex flex-wrap gap-3 justify-center"
        >
          <Link
            to="/offres/consultations-holistiques#consultation"
            data-testid="hero-cta-rdv"
            className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-sand transition-colors duration-500"
          >
            <CalendarDays className="w-4 h-4" /> {t("Prendre rendez-vous pour une consultation")}
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </Link>
          <Link
            to="/contact"
            data-testid="hero-cta-contact"
            className="group inline-flex items-center gap-3 px-6 py-3 rounded-full border border-sand/35 text-sand/90 text-sm tracking-wide backdrop-blur-sm bg-earth/20 hover:border-terra hover:text-terra transition-colors duration-500"
          >
            <MessageCircle className="w-4 h-4" /> {t("Me contacter")}
          </Link>
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 3.4, duration: 1 }}
        className="absolute bottom-8 right-8 lg:right-12 z-[2] hidden sm:flex flex-col items-center gap-3 text-sand/50"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] rotate-90 origin-center translate-y-[-8px]">{t("Dérouler")}</span>
        <ArrowDown className="w-4 h-4 pulse-soft mt-6" />
      </motion.div>
    </section>
  );
};

const Home = () => {
  const { locale, t } = useLocale();
  useSeo({
    title: locale === "en" ? "Entre Les Mondes — Traditional drums, circles, care & place harmonisation · Orbe, Switzerland" : "Entre Les Mondes — Tambours traditionnels, cercles, soins & harmonisation de lieux · Orbe, Suisse",
    description: locale === "en" ? "Ludovic Isoz, Orbe (VD): traditional drum-making workshops, unique handcrafted drums, seasonal drum circles, energy care, geobiology and holistic consultations — in French-speaking Switzerland, in person or remotely." : "Ludovic Isoz, Orbe (VD) : stages de création de tambour traditionnel, tambours artisanaux uniques, cercles de tambours des sabbats, soins énergétiques, géobiologie et consultations holistiques — Suisse romande, en présentiel ou à distance.",
    image: IMAGES.mains,
    jsonLd: [
      { "@type": "WebSite", "@id": "https://entrelesmondes.net/#website", url: "https://entrelesmondes.net", name: "Entre Les Mondes", inLanguage: "fr-CH", publisher: { "@id": "https://entrelesmondes.net/#organisation" } },
      { "@type": "ItemList", name: "Services Entre Les Mondes", itemListElement: HOME_SERVICES.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.titre, url: absolute(s.path) })) },
    ],
  });
  const [products, setProducts] = useState([]);
  const [allEvents, setAllEvents] = useState([]);
  const speciale = allEvents.find((x) => x.type === "stage" && isSpeciale(x));
  const events = speciale ? [speciale, ...allEvents.filter((x) => x.id !== speciale.id).slice(0, 2)] : allEvents.slice(0, 3);
  const [bookingEvent, setBookingEvent] = useState(null);

  useEffect(() => {
    api.products({ disponible: true }).then((p) => setProducts(p.slice(0, 3))).catch(() => {});
    api.events().then((e) => setAllEvents(e.filter((x) => !x.annule))).catch(() => {});
    const h = window.location.hash.slice(1);
    if (h) setTimeout(() => document.getElementById(h)?.scrollIntoView({ behavior: "smooth" }), 600);
  }, []);

  const SectionTitle = ({ eyebrow, titre, texte, lien, light = false }) => (
    <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-14">
      <div>
        <p className={`eyebrow mb-6 ${light ? "text-sand/70" : ""}`}>{eyebrow}</p>
        <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight max-w-2xl ${light ? "" : "text-bark"}`}>{titre}</h2>
        {texte && <p className={`mt-5 max-w-xl leading-relaxed ${light ? "text-sand/70" : "text-fumee"}`}>{texte}</p>}
      </div>
      {lien && <Link to={lien.to} data-testid={lien.testid} className={`link-line text-sm inline-flex items-center gap-2 ${light ? "text-sand/70 hover:text-sand" : "text-fumee hover:text-bark"}`}>{lien.label} <ArrowRight className="w-4 h-4" /></Link>}
    </Reveal>
  );

  const agendaPreview = (mobile = false) => (
    <section id={mobile ? "agenda-mobile" : "agenda"} className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-32 scroll-mt-20" data-testid={mobile ? "agenda-preview-mobile" : "agenda-preview"}>
      <Reveal className="mb-10">
        <p className="eyebrow mb-6">{t("L'agenda")}</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bark leading-tight">{t("Les prochains rendez-vous")}</h2>
      </Reveal>
      <div className="mb-10" data-testid="agenda-preview-meteo"><CielDuMoment compact /></div>
      <Reveal className="flex flex-wrap items-center gap-x-6 gap-y-3 mb-10" data-testid="agenda-preview-liens">
        <a href={WHATSAPP_COMMUNAUTE} target="_blank" rel="noopener noreferrer" data-testid="agenda-preview-whatsapp" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-sage/50 text-bark text-sm hover:bg-sage/15 transition-colors duration-300">
          <MessageCircle className="w-4 h-4 text-sage" /> {t("Recevoir les dates sur WhatsApp")}
        </a>
        {[["tambours", "Stages & tambours"], ["cercles", "Cercles"], ["soins", "Soins"], ["randonnees", "Randonnées"], ["formations", "Formations"]].map(([type, label]) => (
          <Link key={type} to={`/agenda?type=${type}`} data-testid={`agenda-preview-filtre-${type}`} className="text-sm text-fumee hover:text-terra link-line">{t(label)}</Link>
        ))}
        <Link to="/agenda" data-testid="agenda-preview-link" className="link-line text-sm text-bark inline-flex items-center gap-2 ml-auto">
          {t("Tout le calendrier")} <ArrowRight className="w-4 h-4" />
        </Link>
      </Reveal>
      {events.length > 0 ? (
        <div className="grid md:grid-cols-3 gap-6">
          {events.map((e, i) => <EventCard key={e.id} event={e} index={i} onBook={setBookingEvent} />)}
        </div>
      ) : (
        <div className="grid md:grid-cols-3 gap-6">
          {[0, 1, 2].map((i) => <div key={i} className="h-72 rounded-2xl bg-sand-dark/50 animate-pulse" />)}
        </div>
      )}
    </section>
  );

  return (
    <div>
      <Hero />
      <div className="hidden lg:block"><Marquee /></div>

      <HomeQuickAccess />
      <CarnetPromo context="home" />

      <section className="lg:hidden max-w-7xl mx-auto px-6 pt-8" data-testid="home-cercles-paiens-mobile"><CerclesPaiensAnnouncement compact /></section>

      <div className="lg:hidden">{agendaPreview(true)}</div>
      <div className="hidden lg:block"><HomeStages events={allEvents} /></div>

      {/* Explorer — catégories détaillées */}
      <section id="offers-section" className="hidden lg:block bg-sand-dark/40 border-y border-sand-dark scroll-mt-20" data-testid="offers-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 lg:pt-32 pb-8">
          <Reveal>
            <p className="eyebrow mb-6">{t("Explorer")}</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-bark leading-tight max-w-xl">
              {t("Explorer Entre Les Mondes.")}
            </h2>
          </Reveal>
        </div>

        <div id="tambours" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 scroll-mt-20" data-testid="home-univers-tambours">
          <HomeServices products={products} ids={["tambours"]} />
        </div>
      </section>
      <div className="hidden lg:block"><HomeEditionsEncart /><HomeRealisations /></div>

      <section id="accompagnements" className="hidden lg:block bg-sand-dark/40 border-y border-sand-dark scroll-mt-20" data-testid="home-accompagnements">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionTitle eyebrow={t("Accompagnements")} titre={t("Consultations, soins & soin du moment.")} texte={t("Les consultations se réservent sur un créneau ; les soins se demandent simplement, sans rendez-vous, et sont réalisés à distance.")} />
          <HomeServices products={products} ids={["consultations", "soins"]} extras={{ soins: <SoinDuMomentEncart inline /> }} />
        </div>
      </section>

      <section id="lieux" className="hidden lg:block max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 scroll-mt-20" data-testid="home-lieux">
        <SectionTitle eyebrow={t("Lieux & habitats")} titre={t("Géobiologie, nettoyage & harmonisation des lieux.")} lien={{ to: "/offres/lieux-habitats#visite", label: t("Réserver une visite ou demander un devis"), testid: "home-lieux-visite" }} />
        <HomeServices products={products} ids={["lieux"]} />
      </section>

      <section id="participer" className="hidden lg:block bg-sand-dark/40 border-y border-sand-dark scroll-mt-20" data-testid="home-participer">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
          <SectionTitle eyebrow={t("Apprendre & participer")} titre={t("Formations, cercles, randonnées & voyages.")} lien={{ to: "/agenda", label: t("Toutes les dates"), testid: "home-participer-agenda" }} />
          <div className="mb-16"><CerclesPaiensAnnouncement compact /></div>
          <HomeServices products={products} ids={["cercles"]} />
          <div className="mt-16"><HomeSecondary ids={["wyrd", "voyages"]} /></div>
        </div>
      </section>

      <div className="hidden lg:block">{agendaPreview()}</div>

      <div className="hidden lg:block"><GoogleReviews /></div>

      {/* Ludovic & contact */}
      <section className="relative bg-earth grain text-sand overflow-hidden" data-testid="home-final-cta">
        <img src={IMAGES.foret} alt="" aria-hidden="true" loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="relative z-[2] max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-3">
            <img src={PORTRAITS.principal} alt="Ludovic Isoz, artisan de tambours et géobiologue, à Orbe" loading="lazy" className="w-full aspect-[4/5] object-cover rounded-2xl border border-sand/15" data-testid="home-ludovic-photo" />
          </Reveal>
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-6 text-sand/70">Ludovic Isoz — Orbe</p>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("Vingt ans de terrain, un atelier à Orbe.")}</h2>
            <p className="mt-6 text-sand/70 max-w-xl leading-relaxed" data-testid="home-ludovic-texte">{t("Artisan de tambours, praticien énergétique et géobiologue, Ludovic transmet des outils et un cadre — pas une doctrine. Venez avec vos questions, essayez, gardez ce qui a du sens pour vous.")}</p>
            <Link to="/a-propos" data-testid="home-ludovic-lien" className="inline-flex items-center gap-2 mt-6 text-sm link-line text-sand/80 hover:text-sand">{t("Mon parcours & le podcast")} <ArrowRight className="w-4 h-4" /></Link>
          </Reveal>
          <Reveal className="lg:col-span-4 rounded-2xl border border-sand/15 bg-earth/60 backdrop-blur-sm p-8" data-testid="home-contact-bloc">
            <p className="font-serif text-2xl mb-2">{t("Une question, une envie d'essayer ?")}</p>
            <p className="text-sm text-sand/70 leading-relaxed">Atelier : Rue Pierre Viret 8, 1350 Orbe · <a href="tel:+41794606595" className="link-line hover:text-sand" data-testid="home-contact-tel">+41 79 460 65 95</a> · <a href="mailto:ludo@entrelesmondes.net" className="link-line hover:text-sand" data-testid="home-contact-email">ludo@entrelesmondes.net</a></p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contact" data-testid="home-cta-contact" className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-terra text-earth text-sm tracking-wide hover:bg-sand transition-colors duration-500">{t("Nous écrire")} <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/faq" data-testid="home-cta-faq" className="inline-flex items-center gap-3 px-6 py-3 rounded-full border border-sand/40 text-sand text-sm tracking-wide hover:bg-sand/10 transition-colors duration-500">{t("Lire la FAQ")}</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <BookingDialog event={bookingEvent} open={!!bookingEvent} onOpenChange={(v) => !v && setBookingEvent(null)} />
    </div>
  );
};

export default Home;
